/**
 * Sources Dashboard
 *
 * Insights view that pivots emissions across (emission source × time period).
 * Built around the shared `EmissionSourceBreakdownTable`, with a scope filter
 * and a summary of total CO₂e plus per-scope breakdown at the top.
 */

import { useState } from 'react';
import { Leaf, Inbox } from 'lucide-react';
import { MetricCard, FeaturedMetricCard } from '@g17eco/ui-modern/components/ui/metric-card';
import { TabNav, type TabItem } from '@g17eco/ui-modern/components/ui/tab-nav';
import { EmissionSourceBreakdownTable } from '../EmissionSourceBreakdownTable';
import { EmptyStateCard } from '../EmptyStateCard';
import { useGetEmissionsSummaryQuery } from '../../api/emissions';
import { useEmissionsFormatter } from '../../hooks/useEmissionsFormatter';
import { SCOPE_LUCIDE_ICONS, SCOPE_TAILWIND, SCOPE_COLORS } from '../../config/scopeBranding';
import { EmissionScope } from '../../types/emissions';

interface SourcesDashboardProps {
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  includeChildren?: boolean;
}

type ScopeFilter = 'all' | EmissionScope.Scope1 | EmissionScope.Scope2 | EmissionScope.Scope3;

const SCOPE_FILTER_TABS: TabItem<ScopeFilter>[] = [
  { key: 'all', label: 'All Scopes' },
  { key: EmissionScope.Scope1, label: 'Scope 1' },
  { key: EmissionScope.Scope2, label: 'Scope 2' },
  { key: EmissionScope.Scope3, label: 'Scope 3' },
];

export const SourcesDashboard = ({
  initiativeId,
  periodStart,
  periodEnd,
  includeChildren,
}: SourcesDashboardProps) => {
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const { formatCompact } = useEmissionsFormatter();

  const { data: summary, isLoading: summaryLoading } = useGetEmissionsSummaryQuery(
    { initiativeId, periodStart, periodEnd, includeChildren },
    { skip: !initiativeId },
  );

  const scope1 = summary?.byScope?.scope1 ?? 0;
  const scope2 = summary?.byScope?.scope2 ?? 0;
  const scope3 = summary?.byScope?.scope3 ?? 0;
  const Scope1Icon = SCOPE_LUCIDE_ICONS.scope1;
  const Scope2Icon = SCOPE_LUCIDE_ICONS.scope2;
  const Scope3Icon = SCOPE_LUCIDE_ICONS.scope3;

  return (
    <div className='tw:space-y-6'>
      {/* Summary Cards */}
      <div className='tw:grid tw:grid-cols-2 tw:lg:grid-cols-4 tw:gap-4'>
        <FeaturedMetricCard
          label='Total Emissions'
          value={formatCompact(summary?.totalCO2e ?? 0).formatted}
          unit={formatCompact(summary?.totalCO2e ?? 0).unit}
          icon={<Leaf className='tw:size-5 tw:text-white' />}
          isLoading={summaryLoading}
        />
        <MetricCard
          label='Scope 1'
          value={formatCompact(scope1).formatted}
          unit={formatCompact(scope1).unit}
          icon={<Scope1Icon className={`tw:size-5 ${SCOPE_TAILWIND.scope1.icon}`} />}
          iconBg={SCOPE_TAILWIND.scope1.iconBg}
          borderColor={SCOPE_TAILWIND.scope1.border}
          valueColor={SCOPE_COLORS.scope1}
          isLoading={summaryLoading}
        />
        <MetricCard
          label='Scope 2'
          value={formatCompact(scope2).formatted}
          unit={formatCompact(scope2).unit}
          icon={<Scope2Icon className={`tw:size-5 ${SCOPE_TAILWIND.scope2.icon}`} />}
          iconBg={SCOPE_TAILWIND.scope2.iconBg}
          borderColor={SCOPE_TAILWIND.scope2.border}
          valueColor={SCOPE_COLORS.scope2}
          isLoading={summaryLoading}
        />
        <MetricCard
          label='Scope 3'
          value={scope3 > 0 ? formatCompact(scope3).formatted : '—'}
          unit={scope3 > 0 ? formatCompact(scope3).unit : ''}
          icon={<Scope3Icon className={`tw:size-5 ${SCOPE_TAILWIND.scope3.icon}`} />}
          iconBg={SCOPE_TAILWIND.scope3.iconBg}
          borderColor={SCOPE_TAILWIND.scope3.border}
          valueColor={SCOPE_COLORS.scope3}
          isLoading={summaryLoading}
        />
      </div>

      {/* Scope filter */}
      <div className='tw:flex tw:items-center tw:gap-3'>
        <span className='tw:text-sm tw:text-muted-foreground'>Filter by scope:</span>
        <TabNav
          variant='pills'
          activeTab={scopeFilter}
          onChange={setScopeFilter}
          tabs={SCOPE_FILTER_TABS}
        />
      </div>

      {renderBreakdown({
        scopeFilter,
        summaryLoading,
        scopeTotals: { scope1, scope2, scope3 },
        initiativeId,
        periodStart,
        periodEnd,
        includeChildren,
      })}
    </div>
  );
};

interface RenderBreakdownArgs {
  scopeFilter: ScopeFilter;
  summaryLoading: boolean;
  scopeTotals: { scope1: number; scope2: number; scope3: number };
  initiativeId: string;
  periodStart: string;
  periodEnd: string;
  includeChildren?: boolean;
}

const SCOPE_LABEL: Record<EmissionScope, string> = {
  [EmissionScope.Scope1]: 'Scope 1',
  [EmissionScope.Scope2]: 'Scope 2',
  [EmissionScope.Scope3]: 'Scope 3',
};

const renderBreakdown = ({
  scopeFilter,
  summaryLoading,
  scopeTotals,
  initiativeId,
  periodStart,
  periodEnd,
  includeChildren,
}: RenderBreakdownArgs) => {
  // Wait for the summary to resolve before deciding whether to show the
  // empty state — otherwise on first paint we flash "No emissions data" even
  // for scopes that do have data.
  if (summaryLoading) {
    return (
      <EmissionSourceBreakdownTable
        initiativeId={initiativeId}
        periodStart={periodStart}
        periodEnd={periodEnd}
        scope={scopeFilter === 'all' ? undefined : scopeFilter}
        includeChildren={includeChildren}
      />
    );
  }

  const selectedTotal =
    scopeFilter === 'all'
      ? scopeTotals.scope1 + scopeTotals.scope2 + scopeTotals.scope3
      : scopeFilter === EmissionScope.Scope1
        ? scopeTotals.scope1
        : scopeFilter === EmissionScope.Scope2
          ? scopeTotals.scope2
          : scopeTotals.scope3;

  if (selectedTotal <= 0) {
    const scopeName = scopeFilter === 'all' ? 'any scope' : SCOPE_LABEL[scopeFilter];
    return (
      <EmptyStateCard
        title={scopeFilter === 'all' ? 'No emissions data' : `No ${SCOPE_LABEL[scopeFilter]} data`}
        message={
          scopeFilter === 'all'
            ? 'No calculated emissions exist for this period. Add activities to see a breakdown by source.'
            : `No calculated ${SCOPE_LABEL[scopeFilter]} emissions exist for this period. Add ${scopeName} activities or switch to a different scope.`
        }
        icon={<Inbox className='tw:size-10' />}
      />
    );
  }

  return (
    <EmissionSourceBreakdownTable
      initiativeId={initiativeId}
      periodStart={periodStart}
      periodEnd={periodEnd}
      scope={scopeFilter === 'all' ? undefined : scopeFilter}
      includeChildren={includeChildren}
    />
  );
};
